import * as React from 'react';
import { 
  Box,
  CircularProgress,
  DialogContent,
  IconButton,
  Autocomplete,
  TextField,
  Alert
} from '@mui/material';

import { useDispatch, useSelector } from 'react-redux';
import CloseIcon from '@mui/icons-material/Close';
import { CDialog, CDialogTitle, CDialogActions, CButton, CTextField } from '../../components/common/Modal';

import { 
  createDimVector, 
  updateDimVector,
  getMembersByDimension
} from '../../slices/dimVector';
import cubeService from '../../services/cube';

export default function MemberEmbeddingModal(props) {
  const {open, mode, data, handleClose, onSuccess} = props;

  const [loading, setLoading] = React.useState(false);
  const [cubes, setCubes] = React.useState([]);
  const [dimensions, setDimensions] = React.useState([]);
  const [members, setMembers] = React.useState([]);
  
  const [selectedCube, setSelectedCube] = React.useState(null);
  const [selectedDimension, setSelectedDimension] = React.useState(null);
  const [selectedMember, setSelectedMember] = React.useState(null);
  const [keyword, setKeyword] = React.useState("");
  const [id, setId] = React.useState('');

  const dispatch = useDispatch();
  const membersByDim = useSelector(state => state.dimVector?.members || {});

  // Memoize the onChange handlers to prevent unnecessary re-renders
  const handleCubeChange = React.useCallback((event, newValue) => {
    setSelectedCube(newValue);
    setSelectedDimension(null); // Reset dimension when cube changes
    setSelectedMember(null); // Reset member when cube changes
  }, []);

  const handleDimensionChange = React.useCallback((event, newValue) => {
    setSelectedDimension(newValue);
    setSelectedMember(null); // Reset member when dimension changes
  }, []);

  const handleMemberChange = React.useCallback((event, newValue) => {
    setSelectedMember(newValue);
  }, []);

  // Load cubes on mount
  React.useEffect(() => {
    cubeService.getCubes(1, 100)
      .then(res => {
        setCubes(res.items || []);
      })
      .catch(err => console.error('Error loading cubes:', err));
  }, []);

  // Load dimensions when cube is selected
  React.useEffect(() => {
    if (selectedCube) {
      cubeService.getCube(selectedCube.id)
        .then(res => {
          setDimensions(res.dimensions || []);
        })
        .catch(err => console.error('Error loading dimensions:', err));
    } else {
      setDimensions([]);
    }
  }, [selectedCube]);

  // Load members when dimension is selected
  React.useEffect(() => {
    if (selectedDimension) {
      const dimId = selectedDimension.oracle_epm_metadata_id;
      // Check if we already have members for this dimension
      const existingMembers = membersByDim[dimId];
      if (existingMembers && existingMembers.length > 0) {
        setMembers(existingMembers);
      } else {
        // Only fetch if we don't have members yet
        dispatch(getMembersByDimension(dimId))
          .then(res => {
            setMembers(res.payload?.members || []);
          })
          .catch(err => console.error('Error loading members:', err));
      }
    } else {
      setMembers([]);
    }
  }, [selectedDimension?.oracle_epm_metadata_id, dispatch]); // Use specific property instead of whole object

  // Initialize form state when modal opens or mode/data changes
  React.useEffect(() => {
    if(mode === "Create") {
      setSelectedCube(null);
      setSelectedDimension(null);
      setSelectedMember(null);
      setKeyword("");
    } else if (data) {
      setId(data.id);
      setKeyword(data.keyword || "");
      
      // Find and set the cube
      const cube = cubes.find(c => c.id === data.cube_id);
      if (cube) {
        setSelectedCube(cube);
      }
    }
  }, [mode, data, cubes]);

  // Set dimension and member for edit mode after cubes/dimensions/members are loaded
  React.useEffect(() => {
    if (mode === "Edit" && data && selectedCube) {
      // Set dimension (will be available after cube is loaded)
      if (data.dim_id) {
        const dim = dimensions.find(d => d.oracle_epm_metadata_id === data.dim_id);
        if (dim) {
          setSelectedDimension(dim);
        }
      }
    }
  }, [mode, data, selectedCube, dimensions]);

  // Set member for edit mode after dimension is loaded
  React.useEffect(() => {
    if (mode === "Edit" && data && selectedDimension) {
      // Set member (will be available after dimension is loaded)
      if (data.member_id) {
        const member = members.find(m => m.id === data.member_id);
        if (member) {
          setSelectedMember(member);
        }
      }
    }
  }, [mode, data, selectedDimension, members]);

  const handleSave = (e) => {
    if (!selectedCube || !selectedDimension || !selectedMember || !keyword.trim()) {
      alert('Please fill in all required fields');
      return;
    }

    setLoading(true);
    
    const embeddingData = {
      cube_id: selectedCube.id,
      dim_id: selectedDimension.oracle_epm_metadata_id,
      member_id: selectedMember.id,
      keyword: keyword.trim()
    };

    if (mode === 'Create') {
      dispatch(createDimVector(embeddingData))
        .then(res => {
          setLoading(false);
          if (onSuccess) {
            onSuccess();
          }
          handleClose();
        })
        .catch(err => {
          console.log(err);
          setLoading(false);
          alert('Error creating member embedding: ' + (err.message || 'Unknown error'));
        });
    } else {
      dispatch(updateDimVector({ dimVectorId: id, dimVectorData: embeddingData }))
        .then(res => {
          setLoading(false);
          if (onSuccess) {
            onSuccess();
          }
          handleClose();
        })
        .catch(err => {
          console.log(err);
          setLoading(false);
          alert('Error updating member embedding: ' + (err.message || 'Unknown error'));
        });
    }
  };

  return (
    <>
      <CDialog
        open={open}
        onClose={handleClose}
        maxWidth={"md"}
        fullWidth
      >
        <CDialogTitle>
          {mode === 'Create' ? 'New Member Embedding' : 'Edit Member Embedding'}
        </CDialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 6,
            top: 6,
            color: (theme) => theme.palette.grey[800],
          }}
        >
          <CloseIcon fontSize='small' />
        </IconButton>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {/* Cube Selection */}
            <Autocomplete
              value={selectedCube}
              onChange={handleCubeChange}
              options={cubes}
              getOptionLabel={(option) => option.cube_name || ''}
              disabled={mode === 'Edit'} // Don't allow changing cube in edit mode
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Cube"
                  size="small"
                  required
                />
              )}
            />

            {/* Dimension Selection */}
            <Autocomplete
              value={selectedDimension}
              onChange={handleDimensionChange}
              options={dimensions}
              getOptionLabel={(option) => option.dimension || ''}
              disabled={!selectedCube || mode === 'Edit'} // Don't allow changing dimension in edit mode
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Dimension"
                  size="small"
                  required
                  helperText={!selectedCube ? "Select a cube first" : ""}
                />
              )}
            />

            {/* Member Selection */}
            <Autocomplete
              value={selectedMember}
              onChange={handleMemberChange}
              options={members}
              getOptionLabel={(option) => option.name || ''}
              disabled={!selectedDimension || mode === 'Edit'} // Don't allow changing member in edit mode
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Member"
                  size="small"
                  required
                  helperText={!selectedDimension ? "Select a dimension first" : ""}
                />
              )}
            />

            {/* Keyword */}
            <CTextField
              id="keyword" 
              label="Keyword"
              size="small"
              variant="outlined"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="e.g., Sales Performance, Revenue Analysis, Customer Insights"
              fullWidth
              required
              multiline
              rows={2}
            />

            {selectedCube && selectedDimension && selectedMember && keyword && (
              <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
                <strong>Preview:</strong> When users search for "{keyword}", the system will associate 
                this with the member "{selectedMember.name}" in the {selectedDimension.dimension} dimension 
                of the {selectedCube.cube_name} cube. An embedding vector will be generated automatically 
                to enable semantic search capabilities.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <CDialogActions>
          <CButton variant='outlined' onClick={handleClose}>
            Cancel
          </CButton>
          <CButton variant='contained' onClick={handleSave} disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Save'}
          </CButton>
        </CDialogActions>
      </CDialog>
    </>
  );
}